export const getDateInstance = (date: Date | string | number): Date => {
  if (date instanceof Date) {
    // date is already a Date object
    return date;
  } else if (typeof date === `string`) {
    // date is a string. Check if it can be parsed into a number
    const parsed = Number(date);
    if (isNaN(parsed)) {
      // The string could not be parsed into a number, so treat it as an ISO date string
      return new Date(date);
    } else {
      // The string could be parsed into a number, so treat it as a timestamp
      return new Date(parsed);
    }
  } else {
    // date is a timestamp (number)
    return new Date(date);
  }
};

/**
 * Converts a date string to ISO 8601 format with midnight UTC time
 * @param date - Date string in YYYY-MM-DD format
 * @returns ISO 8601 string with T00:00:00.000Z time, or undefined if date is not provided
 * @example
 * parseDateToISOString('2024-01-15') // Returns '2024-01-15T00:00:00.000Z'
 */
export const parseDateToISOString = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  if (date.includes(`T`)) return date;
  return `${date}T00:00:00.000Z`;
};

/**
 * Formats a date string (ISO or YYYY-MM-DD) as a calendar date
 * WITHOUT applying timezone conversion. Use this for dates that
 * represent calendar days (like start dates, termination dates)
 * rather than moments in time.
 *
 * This solves the timezone shift problem where UTC midnight dates
 * display as the previous day in western timezones.
 *
 * @param dateStr - Date string in ISO format or YYYY-MM-DD
 * @param formatStr - date-fns format string (default: 'MM/dd/yy')
 * @returns Formatted date string or undefined
 * @example
 * formatCalendarDate('2024-12-09T00:00:00.000Z', 'MM/dd/yy') // Returns '12/09/24' in any timezone
 * formatCalendarDate('2024-12-09', 'MMM dd, yyyy') // Returns 'Dec 09, 2024'
 */
export const formatCalendarDate = (
  dateStr: string | undefined | null,
  formatStr: string = 'MM/dd/yy',
): string | undefined => {
  if (!dateStr) return undefined;

  // Extract just the date portion (handles both "2024-12-09" and "2024-12-09T00:00:00.000Z")
  const dateOnly = dateStr.slice(0, 10);
  const parts = dateOnly.split('-');
  if (parts.length !== 3) return undefined;

  const [yearStr, monthStr, dayStr] = parts;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined;

  // Create a local date at noon (avoids any DST edge cases)
  const localDate = new Date(year, month - 1, day, 12, 0, 0);

  // Use Intl.DateTimeFormat for formatting to avoid importing date-fns here
  // Map common format strings to Intl options
  const formatOptions = getIntlFormatOptions(formatStr);
  if (formatOptions) {
    return new Intl.DateTimeFormat('en-US', formatOptions).format(localDate);
  }

  // Fallback: manual formatting for common patterns
  return manualFormat(localDate, formatStr);
};

const getIntlFormatOptions = (formatStr: string): Intl.DateTimeFormatOptions | null => {
  switch (formatStr) {
    case 'MM/dd/yy':
      return {month: '2-digit', day: '2-digit', year: '2-digit'};
    case 'MM/dd/yyyy':
      return {month: '2-digit', day: '2-digit', year: 'numeric'};
    case 'MMM dd, yyyy':
      return {month: 'short', day: '2-digit', year: 'numeric'};
    case "MMM dd', ' yyyy":
      return {month: 'short', day: '2-digit', year: 'numeric'};
    default:
      return null;
  }
};

const manualFormat = (date: Date, formatStr: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const pad = (n: number) => n.toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  switch (formatStr) {
    case 'MM/dd/yy':
      return `${pad(month)}/${pad(day)}/${year.toString().slice(-2)}`;
    case 'MM/dd/yyyy':
      return `${pad(month)}/${pad(day)}/${year}`;
    case 'MMM dd, yyyy':
    case "MMM dd', ' yyyy":
      return `${monthNames[month - 1]} ${pad(day)}, ${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${pad(month)}-${pad(day)}`;
    default:
      return `${pad(month)}/${pad(day)}/${year.toString().slice(-2)}`;
  }
};
